import type { CaptureErrorKind } from '../audio/capture';

export type ScreenState =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'failed'; error: CaptureErrorKind }
  | { kind: 'running' };

export type Browser = 'safari-ios' | 'chrome-android' | 'other';

export function detectBrowser(ua: string): Browser {
  // Na iOS jsou všechny prohlížeče WebKit a oprávnění mikrofonu se řeší stejně.
  if (/iPhone|iPad|iPod/.test(ua)) return 'safari-ios';
  if (/Android/.test(ua) && /Chrome\//.test(ua)) return 'chrome-android';
  return 'other';
}

export function denialInstructions(browser: Browser): string[] {
  switch (browser) {
    case 'safari-ios':
      return [
        'Klepni na „aA“ (nebo ikonu nastavení stránky) v adresním řádku → Nastavení webu → Mikrofon → Povolit.',
        'Nebo: Nastavení → Safari → Mikrofon → Povolit.',
        'Pak stránku znovu načti.',
      ];
    case 'chrome-android':
      return [
        'Klepni na ikonu zámku vedle adresy → Oprávnění → Mikrofon → Povolit.',
        'Nebo: Nastavení Chrome → Nastavení webu → Mikrofon.',
        'Pak stránku znovu načti.',
      ];
    default:
      return ['Povol mikrofon pro tuto stránku v nastavení prohlížeče.', 'Pak stránku znovu načti.'];
  }
}

const FAILURE_TEXT: Record<Exclude<CaptureErrorKind, 'denied'>, string> = {
  'no-device': 'Nenašel se žádný mikrofon.',
  'insecure-context': 'Stránka musí běžet přes HTTPS, jinak prohlížeč mikrofon nepovolí.',
  unsupported: 'Tento prohlížeč nepodporuje přístup k mikrofonu.',
  unknown: 'Mikrofon se nepodařilo spustit.',
};

export function template(state: ScreenState, browser: Browser): string {
  switch (state.kind) {
    case 'idle':
      return `
        <h1>BPM detektor</h1>
        <p>Po klepnutí na Start se zapne mikrofon a aplikace začne měřit tempo hudby. Zvuk zůstává v telefonu.</p>
        <button class="primary" data-action="start">Start</button>`;
    case 'requesting':
      return `
        <h1>BPM detektor</h1>
        <p>Prohlížeč se ptá na mikrofon. Aplikace z něj měří tempo hudby, nic nenahrává ani neodesílá.</p>`;
    case 'failed':
      if (state.error === 'denied') {
        return `
          <h1>Mikrofon je zakázaný</h1>
          <ol>${denialInstructions(browser).map((s) => `<li>${s}</li>`).join('')}</ol>
          <button class="secondary" data-action="reload">Načíst znovu</button>`;
      }
      return `
        <h1>Mikrofon nejde spustit</h1>
        <p>${FAILURE_TEXT[state.error]}</p>
        <button class="secondary" data-action="reload">Zkusit znovu</button>`;
    case 'running':
      return `
        <h1>Diagnostika</h1>
        <section id="diagnostics"></section>
        <button class="secondary" data-action="stop">Stop</button>`;
  }
}

export interface ScreenHandlers {
  onStart(): void;
  onStop(): void;
}

export function renderScreen(root: HTMLElement, state: ScreenState, handlers: ScreenHandlers): void {
  root.innerHTML = template(state, detectBrowser(navigator.userAgent));
  root.querySelector('[data-action="start"]')?.addEventListener('click', handlers.onStart);
  root.querySelector('[data-action="stop"]')?.addEventListener('click', handlers.onStop);
  root.querySelector('[data-action="reload"]')?.addEventListener('click', () => location.reload());
}
